import { BORDER_STYLES, CANVAS_BORDER_TYPE } from '@/common/constants';
import { ptToPx, scaleSize } from './canvas';

/**
 * Calculate dashArray for rect
 *
 * @param   {Number}  width  - the width need to calc
 * @param   {Number}  height  - the height need to calc
 * @param   {String}  strokeType  current stroke type
 * @param   {Number}  strokeWidth  current stroke width
 * @returns {Array<Number>} dashArray
 */
export const getRectDashes = (
  width,
  height,
  strokeType,
  strokeWidth,
  cornerRounding,
  mask = 0
) => {
  if (!strokeWidth) {
    return [];
  }
  let dashArray = [];
  if (strokeType === BORDER_STYLES.ROUND) {
    dashArray = getRoundDashes(
      width,
      height,
      strokeWidth,
      cornerRounding,
      mask
    );
  } else if (strokeType === BORDER_STYLES.SQUARE) {
    if (mask !== 0) {
      const widthArray = getLineDashesEllipse(width, height);
      dashArray = [widthArray, widthArray];
    } else {
      const dashLength = Math.min(Math.min(width, height) * 0.1, 100);

      const widthArray = getLineDashes(
        width,
        0,
        0,
        0,
        dashLength,
        null,
        cornerRounding
      );
      const heightArray = getLineDashes(
        0,
        height,
        0,
        0,
        dashLength,
        null,
        cornerRounding
      );
      dashArray = [widthArray, 0, heightArray, 0, widthArray, 0, heightArray];
      const cornerRoundingPx = ptToPx(cornerRounding);
      const perimeterRadius = 2 * Math.PI * cornerRoundingPx;
      const minDashLength =
        Math.min(widthArray[0], heightArray[0]) - strokeWidth;
      // 4 and 1.5 is const to check break, it can be change to increase or decrease break
      if (perimeterRadius / 4 >= minDashLength / 1.5) {
        dashArray = [widthArray, heightArray, widthArray, heightArray];
      }
    }
  }

  return [].concat(...dashArray);
};

/**
 * Calculate dashArray for rect
 *
 * @param   {Number}  width  - the width need to calc
 * @param   {Number}  height  - the height need to calc
 * @param   {String}  strokeType  current stroke type
 * @param   {Number}  strokeWidth  current stroke width
 * @returns {Array<Number>} dashArray
 */
export const getRectDashesForVideo = (
  width,
  height,
  strokeType,
  strokeWidth
) => {
  if (!strokeWidth) {
    return [];
  }
  let dashArray = [];
  if (strokeType === BORDER_STYLES.ROUND) {
    dashArray = getRoundDashesForVideo(width, height, strokeWidth);
  } else if (strokeType === BORDER_STYLES.SQUARE) {
    const dashLength = Math.min(Math.min(width, height) * 0.1, 100) || 100;
    const widthArray = getLineDashesForVideo(width, 0, 0, 0, dashLength);
    const heightArray = getLineDashesForVideo(0, height, 0, 0, dashLength);
    dashArray = [widthArray, 0, heightArray, 0, widthArray, 0, heightArray];
  }
  return [].concat(...dashArray);
};

/**
 * Calculate dashArray for rect of portrait image
 *
 * @param   {Number}  width  - the width need to calc
 * @param   {Number}  height  - the height need to calc
 * @param   {String}  strokeType  current stroke type
 * @param   {Number}  strokeWidth  current stroke width
 * @param   {Number}  perimeter  perimeter of object
 * @returns {Array<Number>} dashArray
 */
export const getRectDashesForPortrait = (
  width,
  height,
  strokeType,
  strokeWidth,
  perimeter
) => {
  if (!strokeWidth) return [];

  if (strokeType === BORDER_STYLES.ROUND)
    return calcRoundDash(perimeter, strokeWidth);

  if (strokeType === BORDER_STYLES.SQUARE) {
    const dashLength = Math.min(Math.min(width, height) * 0.1, 100);

    return getLineDashes(perimeter, 0, 0, 0, 0, dashLength, true);
  }
};

/**
 * Calculate dashArray repeat base on number of points and dashTemplate
 *
 * @param   {Number}  points  - the length need to calc
 * @param   {Array<Number>}  dashTemplate  current stroke width
 * @returns {Array<Number>} dashArray
 */
const calcDashArray = (points, dashTemplate) => {
  return Array.from({ length: points }).reduce(arr => {
    arr.push(...dashTemplate);
    return arr;
  }, []);
};

/**
 * Calculate dashArray for 1 side of border
 *
 * @param   {Number}  width  - the length need to calc
 * @param   {Number}  strokeWidth  current stroke width
 * @returns {Array<Number>} dashArray for current side
 */
const calcRoundDash = (width, strokeWidth) => {
  let baseSpace = strokeWidth * 2;
  const spaceDivision = width / baseSpace;
  const countDash = Math.floor(spaceDivision);

  if (spaceDivision >= 1.5 && countDash <= 2) {
    return [0, width / 2, 0, width / 2];
  }

  if (countDash <= 1) {
    return [0, width];
  }

  const extra = width % baseSpace;
  baseSpace += extra / countDash;

  return calcDashArray(countDash, [0, baseSpace]);
};

const calcRoundDashFullSingle = (width, strokeWidth, cornerRounding = 0) => {
  const cornerRoundingPx = ptToPx(cornerRounding);
  const perimeterRadius = 2 * Math.PI * cornerRoundingPx;
  const calcWidth = width - 2 * cornerRoundingPx + perimeterRadius / 4;
  let baseSpace = strokeWidth * 2;
  const spaceDivision = calcWidth / baseSpace;
  const countDash = Math.floor(spaceDivision);

  if (spaceDivision >= 1.5 && countDash <= 2) {
    return [0, calcWidth / 2, 0, calcWidth / 2];
  }

  if (countDash <= 1) {
    return [0, calcWidth / 2];
  }

  const extra = calcWidth % baseSpace;
  baseSpace += extra / countDash;

  return [...calcDashArray(countDash, [0, baseSpace])];
};

const calcRoundDashEllipse = (width, height, strokeWidth) => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  let perimeterRectEllipse =
    Math.PI *
    (3 * (halfWidth + halfHeight) -
      Math.sqrt((3 * halfWidth + halfHeight) * (halfWidth + 3 * halfHeight)));
  let baseSpace = strokeWidth * 2;

  const spaceDivision = perimeterRectEllipse / baseSpace;
  const countDash = Math.floor(spaceDivision);

  if (spaceDivision >= 1.5 && countDash <= 2) {
    return [0, perimeterRectEllipse / 4, 0, perimeterRectEllipse / 4];
  }

  if (countDash <= 1) {
    return [0, width];
  }

  const extra = perimeterRectEllipse - baseSpace * countDash;
  baseSpace += extra / countDash;
  return [...calcDashArray(countDash, [0, baseSpace])];
};

/**
 * Calculate points of rounded border
 *
 * @param   {Number}  width  Width of element
 * @param   {Number}  height  Height of element
 * @param   {Number}  strokeWidth  current stroke width
 * @returns {Array<Number>} dashArray for round dash
 */
const getRoundDashes = (
  width,
  height,
  strokeWidth,
  cornerRounding = 0,
  mask = 0
) => {
  if (mask !== 0) {
    const dash = calcRoundDashEllipse(width, height, strokeWidth);
    return dash;
  }
  const dashX = calcRoundDashFullSingle(width, strokeWidth, cornerRounding);
  const dashY = calcRoundDashFullSingle(height, strokeWidth, cornerRounding);

  return [...dashX, ...dashY, ...dashX, ...dashY];
};

/**
 * Calculate points of rounded border for Video
 *
 * @param   {Number}  width  Width of element
 * @param   {Number}  height  Height of element
 * @param   {Number}  strokeWidth  current stroke width
 * @returns {Array<Number>} dashArray for round dash
 */
const getRoundDashesForVideo = (width, height, strokeWidth) => {
  const dashX = calcRoundDash(width, strokeWidth);
  const dashY = calcRoundDash(height, strokeWidth);

  return [...dashX, ...dashY, ...dashX, ...dashY];
};

// same as previous snippet except that it does return all the segment's dashes
const getLineDashes = (
  x1,
  y1,
  x2,
  y2,
  dashLength,
  isSelfClosed,
  cornerRounding = 0
) => {
  const cornerRoundingPx = ptToPx(cornerRounding);

  let length = Math.hypot(x2 - x1, y2 - y1) + 2.75; // calc distance between two points
  const perimeterRadius = 2 * Math.PI * cornerRoundingPx;
  length = length - 2 * cornerRoundingPx + perimeterRadius / 4;
  const gapLength = dashLength;
  const { totalBars, numDashes } = calcDashNumber(
    length,
    dashLength,
    isSelfClosed
  );

  const calcLineLength = totalBars * dashLength;
  const remaining = length - calcLineLength;
  const acctualDashLength = remaining / numDashes + dashLength;

  return Array(totalBars)
    .fill(0)
    .map((_, idx) => (idx % 2 === 0 ? acctualDashLength : gapLength));
};

// same as previous snippet except that it does return all the segment's dashes forvideo
const getLineDashesForVideo = (x1, y1, x2, y2, dashLength, isSelfClosed) => {
  const length = Math.hypot(x2 - x1, y2 - y1) + 2; // calc distance between two points

  const gapLength = dashLength;
  const { totalBars, numDashes } = calcDashNumber(
    length,
    dashLength,
    isSelfClosed
  );

  const calcLineLength = totalBars * dashLength;
  const remaining = length - calcLineLength;
  const acctualDashLength = remaining / numDashes + dashLength;
  return Array(totalBars)
    .fill(0)
    .map((_, idx) => (idx % 2 === 0 ? acctualDashLength : gapLength));
};

// same as previous snippet except that it does return all the segment's dashes
const getLineDashesEllipse = (width, height) => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const length =
    Math.PI *
    (3 * (halfWidth + halfHeight) -
      Math.sqrt((3 * halfWidth + halfHeight) * (halfWidth + 3 * halfHeight)));
  const dashLength = length * 0.02;

  const { totalBars, numDashes } = calcDashNumber(length, dashLength);

  const calcLineLength = totalBars * dashLength;
  const remaining = length - calcLineLength;
  const acctualDashLength = remaining / numDashes + dashLength;

  return Array(totalBars)
    .fill(0)
    .map(() => acctualDashLength);
};

/**
 * To calculate number of dash of particular line
 *
 * @param {Number} length length of dashed line
 * @param {Number} dashLength length of a dash
 * @param {Boolean} isSelfClosed whether it a opened-line or enclosed line
 * @returns {Object} total number of dashes and gaps, number of dashes
 */
const calcDashNumber = (length, dashLength, isSelfClosed) => {
  const dashNumber = Math.floor(length / dashLength);
  const isEven = dashNumber % 2 === 0;

  if (isSelfClosed) {
    const seltClosedTotalBars = isEven ? dashNumber : dashNumber + 1;
    const seltClosedNumDashes = seltClosedTotalBars / 2;
    return { totalBars: seltClosedTotalBars, numDashes: seltClosedNumDashes };
  }

  const totalBars = isEven ? dashNumber + 1 : dashNumber;
  const numDashes = Math.ceil(totalBars / 2);
  return { totalBars, numDashes };
};

export const getStrokeLineCap = strokeType => {
  if (BORDER_STYLES.ROUND === strokeType) {
    return strokeType;
  }
  return CANVAS_BORDER_TYPE.BUTT;
};

/**
 * Get border data from store and set to Rect object
 */
export const setBorderObject = (rectObj, objectData) => {
  const {
    border: { strokeWidth, stroke, strokeLineType }
  } = objectData;

  const strokeLineCap = getStrokeLineCap(strokeLineType);

  rectObj.set({
    strokeWidth: scaleSize(strokeWidth),
    stroke,
    strokeLineCap,
    strokeLineType
  });

  setTimeout(() => {
    rectObj.canvas.renderAll();
  });
};

/**
 * Set border color when selected group object
 * @param {Element}  group  Group object
 * @param {Object}  sheetLayout  current layout of canvas
 */
export const setBorderHighlight = (group, sheetLayout) => {
  group.set({
    borderColor: sheetLayout?.id ? 'white' : '#bcbec0'
  });
};
